import {
  faCircleExclamation,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

/**
 * Generisches Form-Field-Kit, analog zu shadcns `<Field />`-Familie
 * (https://ui.shadcn.com/docs/components/radix/field) — gleiches Handling
 * (`Field` mit `data-invalid`, `FieldContent` gruppiert Label/Description/
 * Error, Steuerelement bekommt `aria-invalid` direkt), aber mit den
 * Figma-V2-Tokens/-Typografie statt shadcns Default-Optik. Figma nutzt exakt
 * dieselbe „Labels"-Zeile und denselben Helfertext identisch in mehreren
 * Component-Sets (Select, Input, …), daher hier generisch statt pro Feld
 * dupliziert.
 *
 * Nur der für `Select`/„Input" benötigte Ausschnitt ist umgesetzt
 * (`Field`, `FieldContent`, `FieldLabel`, `FieldDescription`, `FieldError`).
 * `FieldSet`/`FieldGroup`/`FieldLegend`/`FieldSeparator` aus shadcns Vorlage
 * fehlen bewusst — kein Figma-Beleg/Anwendungsfall dafür bisher.
 */
const fieldVariants = cva("group/field flex w-full gap-1", {
  variants: {
    orientation: {
      vertical: "flex-col",
      horizontal: "flex-row items-center",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
})

type FieldProps = ComponentProps<"div"> & VariantProps<typeof fieldVariants>

/** Wurzel eines Feldes — `data-invalid` steuert die Fehler-Optik der Kinder (`FieldLabel`), analog zu TanStack Forms `field.state.meta.isTouched && !field.state.meta.isValid`. */
function Field({ className, orientation = "vertical", ...props }: FieldProps) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  )
}

function FieldContent({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn("flex flex-1 flex-col gap-1", className)}
      {...props}
    />
  )
}

type FieldLabelProps = ComponentProps<"label"> & {
  requiredField?: boolean
  invalid?: boolean
  /** Figma „Info Button" — Verhalten (z. B. Tooltip/Popover) liegt beim Konsumenten. */
  infoButton?: ReactNode
}

/** Figma „Labels" — `htmlFor` sollte auf die `id` des zugehörigen Steuerelements zeigen. */
function FieldLabel({
  className,
  requiredField = false,
  invalid = false,
  infoButton,
  children,
  ...props
}: FieldLabelProps) {
  return (
    <span
      className={cn(
        "flex h-6 w-full items-center gap-0",
        invalid ? "text-error-red-200" : "text-labels-primary"
      )}
    >
      <label
        data-slot="field-label"
        className={cn(
          "text-xs leading-4 font-bold whitespace-nowrap",
          className
        )}
        {...props}
      >
        {children}
        {requiredField && "*"}
      </label>
      {infoButton}
    </span>
  )
}

/** Figma Helfertext unter einem Feld. */
function FieldDescription({ className, ...props }: ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "w-full text-xs leading-4 font-normal text-labels-secondary",
        className
      )}
      {...props}
    />
  )
}

const fieldErrorVariants = cva(
  "flex items-center gap-1 text-xs leading-4 font-normal text-labels-primary [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        error: "[&_svg]:text-error-red-200",
        warning: "[&_svg]:text-accent-orange-100",
      },
    },
    defaultVariants: {
      variant: "error",
    },
  }
)

type FieldErrorProps = ComponentProps<"div"> &
  VariantProps<typeof fieldErrorVariants> & {
    /** TanStack Form: `field.state.meta.errors`. */
    errors?: Array<{ message?: string } | undefined>
  }

/**
 * Figma-Fehlermeldung unter einem Feld — optisch identisch zu `InlineAlert`
 * (Icon rot, Text `labels-primary`), hier aber unabhängig davon definiert,
 * da `InlineAlert` nur eine einzelne `description` kennt, `FieldError` aber
 * `errors`-Arrays (TanStack Form) oder beliebige `children` rendert.
 */
function FieldError({
  className,
  children,
  errors,
  variant = "error",
  ...props
}: FieldErrorProps) {
  const content =
    children ??
    (errors && errors.length === 1 ? (
      errors[0]?.message
    ) : errors && errors.length > 1 ? (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {errors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>
        )}
      </ul>
    ) : null)

  if (!content) {
    return null
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(fieldErrorVariants({ variant }), className)}
      {...props}
    >
      {errors && errors.length > 0 && (
        <FontAwesomeIcon
          icon={
            variant === "warning" ? faTriangleExclamation : faCircleExclamation
          }
        />
      )}
      {content}
    </div>
  )
}

export { Field, FieldContent, FieldDescription, FieldError, FieldLabel }
export type { FieldErrorProps, FieldLabelProps, FieldProps }
